import React, { Fragment, useState } from "react";
import { Chip, FormControl, Popover, TextField } from "@material-ui/core";
import dayjs from "dayjs";
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { CustomDateSelector } from "pages/DashboardPage/components/LargeGraphPopup";
import { MenuItemPopOver, TitlePopOver } from "modules/Sty";
import { convertSnakeCase, makeKey, objectToArray } from "modules/Utils";
import ReactDatePicker from "react-datepicker";
import { FilterTypeBudget, FilterTypeDate } from "./CampaignFilterTypes";
import CampaignFilterListItems from "./CampaignFilterListItem";
import DropdownArrow from "components/shareable/DropdownArrow";
import ButtonSymphony from "elements/ButtonSymphony";
import useScreen from "Hooks/useScreen";
import { BrandExistsState } from "Hooks/useBrand";
import useBrand from "Hooks/useBrand";
import { useCampaign } from "Hooks/useCampaign";
import Icon from "elements/Icon";
import { CampaignType, PlaybookType, PLAYBOOK_TYPE_INFO, OLD_CAMPAIGN_TYPES_FILTER } from "modules/Const";

// The filter options available to a user
const CAMPAIGN_FILTERS = [
    { name: 'campaign_type', title: 'Campaign Type' },
    { name: 'campaign_status', title: 'Status' },
    { name: 'date', title: 'Date' },
    { name: 'budget', title: 'Budget' },
    // { name: 'release', title: 'Release' }
];

const CAMPAIGN_STATUS_TYPES = [
    { name: 'active', title: 'Active' },
    { name: 'in_review', title: 'In Review' },
    { name: 'awaiting_agent_processing', title: 'Awaiting Processing' },
    { name: 'draft', title: 'Draft' },
    { name: 'paused', title: 'Paused' },
    { name: 'needs_action', title: 'Needs Action' },
    { name: 'finished', title: 'Finished' },
    { name: 'archived', title: 'Archived' }
];

const CampaignFilterOptions = () => {
    const { mobileView } = useScreen();
    const { currentBrand } = useBrand() as BrandExistsState;
    const {
        getCampaigns,
        campaignFilter,
        setCampaignFilter,
        resetCampaignFilter,
        setIsCampaignFilter,
        setIsCampaignsLimitReached,
        setIsCampaignDraftsLimitReached,
        isLoadingCampaigns
    } = useCampaign();

    const [openPopoverIndex, setOpenPopoverIndex] = useState<number | null>(null);

    const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);

    const [campaignStatuses, setCampaignStatuses] = useState<string[]>([]);

    // applies the filters, and sends the update to the parent
    const applyFilters = async () => {
        setIsCampaignFilter(true);
        handleClosePopover();

        const campaignFilterLocal = { ...campaignFilter };
        campaignFilterLocal.campaignStatuses = campaignStatuses;
        const campaignsLocal = await getCampaigns({
            brandId: currentBrand.id,
            params: campaignFilterLocal
        });

        if (campaignsLocal.length < 20) {
            setIsCampaignsLimitReached(true)
        }
    }

    const removeFilter = () => {
        const campaignFilterLocal = { ...campaignFilter };

        switch (openPopoverIndex) {
            case 0:
                campaignFilterLocal.campaignTypes = [];
                break;
            case 1:
                campaignFilterLocal.campaignStatuses = [];
                setCampaignStatuses([]);
                break;
            case 2:
                campaignFilterLocal.dateModifier = 'all_time';
                campaignFilterLocal.startDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
                campaignFilterLocal.endDate = dayjs().format('YYYY-MM-DD');
                campaignFilterLocal.filterDays = '30';
                break;
            case 3:
                campaignFilterLocal.budgetModifier = 'no_budget_filter';
                campaignFilterLocal.budgetAmount1 = '';
                campaignFilterLocal.budgetAmount2 = '';
                break;
            default:
                break;
        }

        setCampaignFilter(campaignFilterLocal);
    }

    const handleSetFilterCampaignTypes = (key: string) => {
        const campaignTypesLocal = [...campaignFilter.campaignTypes];

        const currentIndex = campaignTypesLocal.findIndex((ct) => ct === key)

        if (currentIndex === -1) {
            // Status isn't selected yet, so add it
            campaignTypesLocal.push(key);
        } else {
            // Status is already selected, so remove it
            campaignTypesLocal.splice(currentIndex, 1);
        }

        setCampaignFilter({
            ...campaignFilter,
            campaignTypes: campaignTypesLocal
        });
    }

    const handleSetFilterCampaignStatuses = (filterStatus: string) => {
        const currentIndex = campaignStatuses.indexOf(filterStatus);
        const newSelectedStatuses = [...campaignStatuses];

        if (currentIndex === -1) {
            // Status isn't selected yet, so add it
            newSelectedStatuses.push(filterStatus);
        } else {
            // Status is already selected, so remove it
            newSelectedStatuses.splice(currentIndex, 1);
        }

        setCampaignStatuses(newSelectedStatuses);
    }

    const handleSetFilterDateModifier = (event: SelectChangeEvent) => {
        const dateModifier: FilterTypeDate = event.target.value as FilterTypeDate;
        setCampaignFilter({
            ...campaignFilter,
            dateModifier: dateModifier
        });
    };

    const handleSetBudget = (event: SelectChangeEvent) => {
        const budget: FilterTypeBudget = event.target.value as FilterTypeBudget;
        setCampaignFilter({
            ...campaignFilter,
            budgetModifier: budget
        });
    };

    const handleChangeFilterDays = (event: React.ChangeEvent<HTMLInputElement>) => {
        const days = event.target.value.replace(/\D+/g, '');
        setCampaignFilter({
            ...campaignFilter,
            filterDays: days
        });
    };

    const handleChangeBudgetAmount1 = (event: React.ChangeEvent<HTMLInputElement>) => {
        const budget1 = event.target.value.replace(/\D+/g, '');
        setCampaignFilter({
            ...campaignFilter,
            budgetAmount1: budget1
        });
    };

    const handleChangeBudgetAmount2 = (event: React.ChangeEvent<HTMLInputElement>) => {
        const budget2 = event.target.value.replace(/\D+/g, '');
        setCampaignFilter({
            ...campaignFilter,
            budgetAmount2: budget2
        });
    };

    // returns whether the filter is active or not
    const isFilterActive = (filterName: string) => {
        switch (filterName) {
            case "campaign_type":
                return campaignFilter.campaignTypes.length;
            case "campaign_status":
                return campaignStatuses.length;
            case "date":
                return campaignFilter.dateModifier !== 'all_time';
            case "budget":
                return campaignFilter.budgetModifier !== 'no_budget_filter';
            case "release":
                return false; // TODO: not yet built
            default:
                return null;
        }
    }

    // Returns the string associated with the filter
    const getFilterResultString = (filter: { name: string; title: string }) => {
        let filterResultString = null;
        const darkFont = { color: "#333" };

        switch (filter.name) {
            case "campaign_type":
                if (campaignFilter.campaignTypes.length > 0) {
                    const firstTwoTypes = campaignFilter.campaignTypes.slice(0, 2).map(ctKey => {
                        if (ctKey === 'record_streams') { // TODO CONNER: this is a hack dealing with DB inconsistency
                            ctKey = 'increase_record_streams'
                        }
                        const campaignTypeInfo = PLAYBOOK_TYPE_INFO[ctKey as PlaybookType | CampaignType];
                        return campaignTypeInfo?.title
                    });
                    const remainingCount = campaignFilter.campaignTypes.length - 2;
                    if (remainingCount > 0) {
                        filterResultString = (
                            <>
                                Campaign Type: <span style={darkFont}>{firstTwoTypes.join(", ")}</span> and {remainingCount} more
                            </>
                        );
                    } else {
                        filterResultString = (
                            <>
                                Campaign Type: <span style={darkFont}>{firstTwoTypes.join(", ")}</span>
                            </>
                        );
                    }
                } else {
                    filterResultString = (
                        <>
                            Campaign Type
                        </>
                    )
                }
                break;
            case "campaign_status":
                filterResultString = <>
                    Status {campaignStatuses.length ?
                        <span className="campaignStatusFilterResult" style={darkFont}>
                            : {campaignStatuses.map(cs => convertSnakeCase(cs)).join(", ")}
                        </span>
                        :
                        null
                    }
                </>;
                break;
            case "date":
                const dateModifierString = convertSnakeCase(campaignFilter.dateModifier);

                const startDateString = dayjs(campaignFilter.startDate).format("MMM D, YYYY").toString();
                const endDateString = dayjs(campaignFilter.endDate).format("MMM D, YYYY").toString();
                if (campaignFilter.dateModifier === "in_between") {
                    filterResultString = <>
                        Date Between: <span style={darkFont}>{startDateString} - {endDateString}</span>
                    </>;
                } else if (campaignFilter.dateModifier === "before" || campaignFilter.dateModifier === "after") {
                    filterResultString = <>
                        Date {dateModifierString}: <span style={darkFont}>{startDateString}</span>
                    </>;
                } else if (campaignFilter.dateModifier === "last_days") {
                    filterResultString = <>
                        Last <span style={darkFont}>{campaignFilter.filterDays} days</span>
                    </>;
                } else if (campaignFilter.dateModifier === "all_time") {
                    filterResultString = <>
                        Dates
                    </>;
                }
                break;
            case "budget":
                const filterBudgetString = convertSnakeCase(campaignFilter.budgetModifier);

                if (campaignFilter.budgetModifier === "equal") {
                    filterResultString = <>
                        Budget <span style={darkFont}>{filterBudgetString} to: ${campaignFilter.budgetAmount1.length ? campaignFilter.budgetAmount1 : "0"}</span>
                    </>;
                } else if (campaignFilter.budgetModifier === "greater_than" || campaignFilter.budgetModifier === "less_than") {
                    filterResultString = <>
                        Budget <span style={darkFont}>{filterBudgetString}: ${campaignFilter.budgetAmount1.length ? campaignFilter.budgetAmount1 : "0"}</span>
                    </>;
                } else if (campaignFilter.budgetModifier === "between_two_numbers") {
                    filterResultString = <>
                        Budget Between: <span style={darkFont}>${campaignFilter.budgetAmount1.length ? campaignFilter.budgetAmount1 : "0"} - ${campaignFilter.budgetAmount2.length ? campaignFilter.budgetAmount2 : "0"}</span>
                    </>;
                } else if (campaignFilter.budgetModifier === "no_budget_filter") {
                    filterResultString = <>
                        Budget
                    </>;
                }

                break;
            default:
                filterResultString = null;
                break;
        }

        return filterResultString;
    }

    const renderCustomDatesPicker = () => {
        if (campaignFilter.dateModifier === "all_time") {
            return null
        }

        if (campaignFilter.dateModifier === "last_days") {
            return (
                <TextField
                    label="Last X Days"
                    variant="outlined"
                    value={campaignFilter.filterDays}
                    onChange={handleChangeFilterDays}
                    style={{ marginBottom: '10px' }}
                    placeholder="Enter a number..."
                />
            );
        }

        return (
            <>
                {campaignFilter.dateModifier === "in_between" && <p className="mr-1">From:</p>}
                <ReactDatePicker
                    filterDate={(date: Date) => {
                        return dayjs(date) < dayjs(campaignFilter.endDate).subtract(1, 'day');
                    }}
                    selected={dayjs(campaignFilter.startDate).toDate()}
                    onChange={(date) => {
                        const campaignFilterLocal = { ...campaignFilter }
                        campaignFilterLocal.startDate = dayjs(date).format("YYYY-MM-DD")
                        setCampaignFilter(campaignFilterLocal)
                    }}
                    customInput={<CustomDateSelector />}
                    onCalendarOpen={() => setIsDatePickerOpen(true)}
                    onCalendarClose={() => setIsDatePickerOpen(false)}
                />
                {campaignFilter.dateModifier === "in_between" &&
                    <>
                        <p className="mr-1">To:</p>
                        <ReactDatePicker
                            minDate={dayjs(campaignFilter.startDate).add(1, 'day').toDate()}
                            selected={dayjs(campaignFilter.endDate).toDate()}
                            onChange={(date) => {
                                const campaignFilterLocal = { ...campaignFilter }
                                campaignFilterLocal.endDate = dayjs(date).format("YYYY-MM-DD")
                                setCampaignFilter(campaignFilterLocal)
                            }}
                            customInput={<CustomDateSelector />}
                        />
                    </>
                }
            </>
        );
    }

    const renderDateFilter = () => {
        return (
            <div
                className="dateFilter"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    maxWidth: 340,
                    height: isDatePickerOpen ? "400px" : "auto",
                    overflow: isDatePickerOpen ? "hidden" : "visible"
                }}
            >
                <TitlePopOver>
                    Select Date Range
                </TitlePopOver>
                <FormControl fullWidth>
                    <Select
                        labelId="filter-select-label"
                        id="filter-select-date"
                        value={campaignFilter.dateModifier}
                        label="Date Filter"
                        onChange={handleSetFilterDateModifier}
                        sx={{ fontFamily: "DIN" }}
                        IconComponent={() =>
                            <DropdownArrow
                                marginRight={10}
                                width={24}
                                height={24}
                                direction={"down"}
                            />
                        }
                    >
                        <MenuItemPopOver value="all_time">All-Time</MenuItemPopOver>
                        <MenuItemPopOver value="in_between">In-Between</MenuItemPopOver>
                        <MenuItemPopOver value="before">Before</MenuItemPopOver>
                        <MenuItemPopOver value="after">After</MenuItemPopOver>
                        <MenuItemPopOver value="last_days">Last Days</MenuItemPopOver>
                    </Select>
                </FormControl>

                <div className="flex items-center justify-center">
                    {renderCustomDatesPicker()}
                </div>
            </div>
        );
    }

    const renderBudgetFilter = () => {
        return (
            <div
                className="budgetFilterContainer"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                    maxWidth: 340
                }}
            >
                <TitlePopOver>
                    Filter by Budget
                </TitlePopOver>
                <FormControl fullWidth style={{ fontFamily: "DIN" }}>
                    <Select
                        labelId="filter-select-label"
                        id="filter-select-budget"
                        onChange={handleSetBudget}
                        value={campaignFilter.budgetModifier}
                        sx={{ fontFamily: "DIN" }}
                        IconComponent={() =>
                            <DropdownArrow
                                marginRight={10}
                                width={24}
                                height={24}
                                direction={"down"}
                            />
                        }
                    >
                        <MenuItemPopOver value="no_budget_filter" disabled>Select a budget filter</MenuItemPopOver>
                        <MenuItemPopOver value="equal">Equal to</MenuItemPopOver>
                        <MenuItemPopOver value="greater_than">Greater than</MenuItemPopOver>
                        <MenuItemPopOver value="less_than">Less than</MenuItemPopOver>
                        <MenuItemPopOver value="between_two_numbers">Between two numbers</MenuItemPopOver>
                    </Select>
                </FormControl>

                <TextField
                    label="Budget Amount"
                    variant="outlined"
                    value={`$${campaignFilter.budgetAmount1}`}
                    onChange={handleChangeBudgetAmount1}
                    placeholder="Enter a number..."
                    style={{ fontFamily: "DIN" }}
                    InputProps={{ style: { fontFamily: "DIN" } }}
                />

                {campaignFilter.budgetModifier === "between_two_numbers" &&
                    <TextField
                        label="Budget Amount Upper Limit"
                        variant="outlined"
                        value={`$${campaignFilter.budgetAmount2}`}
                        onChange={handleChangeBudgetAmount2}
                        style={{ marginBottom: '10px', fontFamily: "DIN" }}
                        InputProps={{ style: { fontFamily: "DIN" } }}
                        placeholder="Enter a number..."
                    />
                }
            </div>
        );
    }

    const renderReleaseFilter = () => {
        return (
            <div className="filterByReleaseText">
                Filter by Release
            </div>
        );
    }

    const renderPopoverContents = (filter: { name: string; title: string }) => {
        let body = null;
        switch (filter.name) {
            case "campaign_type":
                body = (
                    <CampaignFilterListItems
                        title="Filter by Campaign Type"
                        listItems={objectToArray(PLAYBOOK_TYPE_INFO)
                            .filter((playbook) => !OLD_CAMPAIGN_TYPES_FILTER[playbook.key as CampaignType])
                            .map((playbook) => {
                                const {
                                    key,
                                    title,
                                    icon
                                } = playbook;
                                return {
                                    key: key,
                                    onClick: handleSetFilterCampaignTypes,
                                    icon,
                                    checked: campaignFilter.campaignTypes.some(ct => ct === key),
                                    label: title
                                }
                            })}
                    />
                )
                break;
            case "campaign_status":
                body = (
                    <CampaignFilterListItems
                        title="Filter by status"
                        listItems={CAMPAIGN_STATUS_TYPES
                            .map((campaignStatusType) => {
                                const key = campaignStatusType.name;
                                const itemTitle = campaignStatusType.title;
                                const checked = campaignStatuses.some(ct => ct === key);
                                return {
                                    key: key,
                                    onClick: handleSetFilterCampaignStatuses,
                                    checked: checked,
                                    label: itemTitle
                                }
                            })}
                    />
                )
                break;
            case "date":
                body = renderDateFilter();
                break;
            case "budget":
                body = renderBudgetFilter();
                break;
            case "release":
                body = renderReleaseFilter();
                break;
            default:
                body = null;
                break;
        }

        return (
            <>
                {body}
                {renderApplyButton()}
            </>
        )
    }

    // when a user closes popover, and filters that weren't applied are reset
    const handleClosePopover = () => {
        setOpenPopoverIndex(null);
    };

    const handleClickFilterOption = (index: number) => (event: React.MouseEvent<HTMLDivElement>) => {
        setOpenPopoverIndex(openPopoverIndex === index ? null : index); // Toggle popover
    };

    const renderApplyButton = (loading?: boolean) => {
        return (
            <>
                <ButtonSymphony
                    className="campaignTypesFilterApplyButton"
                    style={{ marginLeft: "auto", marginRight: "auto" }}
                    onClick={applyFilters}
                    disabled={loading}
                    isLoading={loading}
                    size="small"
                >
                    Apply
                </ButtonSymphony>
                <ButtonSymphony
                    className="campaignTypesFilterClearButton"
                    style={{ marginLeft: "auto", marginRight: 'auto' }}
                    onClick={removeFilter}
                    variant="text"
                >
                    Clear
                </ButtonSymphony>
            </>
        );
    }

    // NOTE: render()
    return (
        <div
            className="campaignTypesFilterContainer"
            style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
                paddingBottom: mobileView ? 24 : 16,
            }}
        >
            <div style={{ whiteSpace: "nowrap" }}>Filter by:</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {CAMPAIGN_FILTERS.map((filter, filterIndex) => {
                    const filterChipId = `${filter.name}-${filterIndex}`;
                    return (
                        <Fragment key={makeKey('key', filterChipId)}>
                            <Chip
                                id={filterChipId}
                                style={{
                                    backgroundColor: isFilterActive(filter.name) ? "rgba(136, 0, 255, 0.08)" : "#EBEBEB",
                                    color: isFilterActive(filter.name) ? "#8800FF" : "#777",
                                }}
                                label={getFilterResultString(filter)}
                                onClick={handleClickFilterOption(filterIndex)}
                            />
                            <Popover
                                id={filterChipId}
                                open={openPopoverIndex === filterIndex}
                                anchorEl={document.getElementById(filterChipId)}
                                onClose={handleClosePopover}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <div
                                    className="popoverContents"
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        padding: 16,
                                        flexDirection: 'column'
                                    }}
                                >
                                    {renderPopoverContents(filter)}
                                </div>
                            </Popover>
                        </Fragment>
                    )
                })}
            </div>
            <Icon
                className="campaignTypesFilterResetButton"
                onClick={() => {
                    resetCampaignFilter(currentBrand.id)
                    setIsCampaignsLimitReached(false)
                    setIsCampaignDraftsLimitReached(false)
                }}
                icon="reload"
                isLoading={isLoadingCampaigns}
            />
        </div>
    )
}

export default CampaignFilterOptions